import _get from "lodash/get";
import _isString from "lodash/isString";
import moment, { Moment } from "moment";
import { CART_ITEM_TYPE, DELIVERY_TYPE, DeliveryType, PAYMENT_METHOD, PaymentMethod } from "../../../constants";
import PAGES from "../../../constants/pages";
import { ICartItem } from "../../../models";
import { isBroadbandCode } from "../../../shared/subscriptionCodeUtils";

const DATE_FORMAT_DDMMYYYY_WITH_SEPARATION = (separator: string) => ["DD", "MM", "YYYY"].join(separator);
// Birth dates may arrive as DD.MM.YYYY (form input) or YYYY-MM-DD (from Redux store / legal owner)
const BIRTH_DATE_FORMATS = [DATE_FORMAT_DDMMYYYY_WITH_SEPARATION("."), "YYYY-MM-DD"];
const parseBirthDate = (date: string) => moment(date, BIRTH_DATE_FORMATS, true);

export const isOldEnoughToBeLegalOwner = (age: number) => age >= 18;

export const isValidSubscriptionAge = ({
    legalOwnerBirthDate,
    subscriptionMaxAgeAllowed,
    productUserBirthDate,
    useProductUserAge,
    now = moment(),
}: {
    legalOwnerBirthDate?: string;
    now?: Moment;
    productUserBirthDate?: string;
    subscriptionMaxAgeAllowed?: number;
    useProductUserAge?: boolean;
}) => {
    if (!subscriptionMaxAgeAllowed) {
        return true;
    }

    if (!useProductUserAge) {
        if (!legalOwnerBirthDate) {
            // Invalid birth date. Problem will be handled later.
            return true;
        }
        const legalOwnerBirthDateMoment = parseBirthDate(legalOwnerBirthDate);
        if (!legalOwnerBirthDateMoment.isValid()) {
            // Unparseable birth date. Problem will be handled later.
            return true;
        }
        const legalOwnerAge = now.diff(legalOwnerBirthDateMoment, "years", false);
        return isOldEnoughToBeLegalOwner(legalOwnerAge) && legalOwnerAge < subscriptionMaxAgeAllowed;
    } else {
        if (!productUserBirthDate) {
            return true;
        }
    }

    const productUserBirthDateMoment = parseBirthDate(productUserBirthDate ?? "");
    if (!productUserBirthDate || !productUserBirthDateMoment.isValid()) {
        // Missing or unparseable birth date — defer to server/credit check
        return true;
    }
    const productUserAge = now.diff(productUserBirthDateMoment, "years", false);

    return productUserAge < subscriptionMaxAgeAllowed;
};

export const isPhoneNumberUsable = (phoneNumber: string) =>
    _isString(phoneNumber) && (/^[94]\d{7}$/.test(phoneNumber) || /^[5]\d{11}$/.test(phoneNumber));

export const getPaymentMethodImages = (method: PaymentMethod) => {
    const credCardImages = [
        { url: "/shop/assets/img-static/payment/mastercard.svg", alt: "Mastercard" },
        { url: "/shop/assets/img-static/payment/visa.svg", alt: "Visa" },
    ];
    const vippsImages = [{ url: "/shop/assets/img-static/payment/vipps.png", alt: "Vipps" }];

    switch (method) {
        case PAYMENT_METHOD.NETS:
        case PAYMENT_METHOD.NETS_3D:
        case PAYMENT_METHOD.ADYEN_CC:
            return credCardImages;
        case PAYMENT_METHOD.VIPPS:
        case PAYMENT_METHOD.VIPPS_v2:
            return vippsImages;
        default:
            return [];
    }
};

export const isDeliveryTypeTracked = (method?: DeliveryType): boolean =>
    typeof method !== "undefined" &&
    !([DELIVERY_TYPE.PICK_UP_IN_SHOP, DELIVERY_TYPE.LETTER] as readonly string[]).includes(method);

export const pending = (actionType: string) => {
    return `${actionType}_PENDING`;
};
export const fulfilled = (actionType: string) => {
    return `${actionType}_FULFILLED`;
};
export const rejected = (actionType: string) => {
    return `${actionType}_REJECTED`;
};

export const getListingPageForItem = (item?: Pick<ICartItem, "type" | "id" | "href">): string => {
    const itemType = _get(item, "type");
    const isMbbSubscription = isBroadbandCode(_get(item, "id"));

    if (!item) return PAGES.LISTING_PAGES.MOBILE.URL;

    switch (true) {
        case isMbbSubscription:
        case itemType === CART_ITEM_TYPE.MODEM:
            return PAGES.LISTING_PAGES.MBB.URL;
        case itemType === CART_ITEM_TYPE.SUBSCRIPTION:
        case itemType === CART_ITEM_TYPE.SUBSCRIPTION_DRAFT:
            return item.href as string;
        case itemType === CART_ITEM_TYPE.HANDSET:
        case itemType === CART_ITEM_TYPE.HANDSET_PREOWNED:
            return PAGES.LISTING_PAGES.MOBILE.URL;
        case itemType === CART_ITEM_TYPE.WEARABLE:
            return PAGES.LISTING_PAGES.WEARABLE.URL;
        case itemType === CART_ITEM_TYPE.TABLET:
            return PAGES.LISTING_PAGES.TABLET.URL;
        case itemType === CART_ITEM_TYPE.ACCESSORY:
            return PAGES.LISTING_PAGES.ACCESSORY.URL;
        case itemType === CART_ITEM_TYPE.CONNECTED_DEVICE:
            return PAGES.LISTING_PAGES.CONNECTED_DEVICE.URL;
        case itemType === CART_ITEM_TYPE.SPECIALPRODUCT:
            return `${PAGES.LISTING_PAGES.SIMONLY.URL}kontantkort`;
        default:
            return PAGES.LISTING_PAGES.MOBILE.URL;
    }
};
